import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import CourierLayout from '../CourierLayout';
import { API } from '../../../config';


const CourierProfile = () => {
  const [profile, setProfile] = useState({
    username: '',
    email: '',
    accountType: 'Individual Driver',
    driverLicenseNumber: '',
    driverLicenseState: '',
    driverLicenseExpiration: '',
    insurancePolicyNumber: '',
    insuranceExpiration: '',
    liftCapacity: '',
  });

  const [vehicle, setVehicle] = useState({
    make: '',
    model: '',
    color: '',
    year: '',
    licensePlate: '',
    equipment: '',
    vehicleType: '',
    cargoCapacity: '',
  });

  const [carrier, setCarrier] = useState({
    authorityNumber: '',
    dotNumber: '',
    mcNumber: '',
  });

  const [message, setMessage] = useState('');

  const [stripeStatus, setStripeStatus] = useState({
  connected: false,
  onboardingComplete: false,
  chargesEnabled: false,
  payoutsEnabled: false,
});

const [stripeLoading, setStripeLoading] = useState(false);

  const isProfessionalCarrier = profile.accountType === 'Professional Carrier';

  const today = new Date();
   today.setHours(0, 0, 0, 0);

const currentYear = new Date().getFullYear();

const isValidEmail = (value) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
};

const isValidUsername = (value) => {
  return /^[a-zA-Z0-9._-]{3,30}$/.test(String(value || '').trim());
};

const isFutureDate = (value) => {
  if (!value) return false;

  const date = new Date(value);
  date.setHours(0, 0, 0, 0);

  return Number.isFinite(date.getTime()) && date > today;
};

const isValidStateCode = (value) => {
  return /^[A-Za-z]{2}$/.test(String(value || '').trim());
};

const isPositiveNumber = (value) => {
  if (value === '' || value === null || value === undefined) return true;
  return Number(value) > 0;
};

const isValidVehicleYear = (value) => {
  const year = Number(value);

  return (
    Number.isInteger(year) &&
    year >= 1980 &&
    year <= currentYear + 1
  );
};

  const handleProfileChange = (field, value) => {
    setProfile((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleVehicleChange = (field, value) => {
    setVehicle((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCarrierChange = (field, value) => {
    setCarrier((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateProfile = () => {
  const username = profile.username.trim();
  const email = profile.email.trim();

  if (username && !isValidUsername(username)) {
    setMessage('Username must be 3–30 characters and may contain letters, numbers, dots, underscores, or hyphens.');
    return false;
  }

  if (email && !isValidEmail(email)) {
    setMessage('Please enter a valid email address.');
    return false;
  }

  if (!profile.driverLicenseNumber.trim()) {
    setMessage('Driver license number is required.');
    return false;
  }

  if (!profile.driverLicenseState.trim()) {
    setMessage('Driver license issuing state is required.');
    return false;
  }

  if (!isValidStateCode(profile.driverLicenseState)) {
    setMessage('Issuing state must be a valid 2-letter state code, for example CO, NY, TX.');
    return false;
  }

  if (!profile.driverLicenseExpiration) {
    setMessage('Driver license expiration date is required.');
    return false;
  }

  if (!isFutureDate(profile.driverLicenseExpiration)) {
    setMessage('Driver license expiration date must be in the future.');
    return false;
  }

  if (!profile.insurancePolicyNumber.trim()) {
    setMessage('Insurance policy number is required.');
    return false;
  }

  if (!profile.insuranceExpiration) {
    setMessage('Insurance expiration date is required.');
    return false;
  }

  if (!isFutureDate(profile.insuranceExpiration)) {
    setMessage('Insurance expiration date must be in the future.');
    return false;
  }

  if (!isPositiveNumber(profile.liftCapacity)) {
    setMessage('Lift & carry capacity must be a positive number.');
    return false;
  }

  if (isProfessionalCarrier && !carrier.dotNumber.trim() && !carrier.mcNumber.trim()) {
    setMessage('Professional carriers should provide a DOT Number or MC Number.');
    return false;
  }

  return true;
};

  const saveProfile = () => {
    if (!validateProfile()) return;
    setMessage('Profile information saved.');
  };

  const validateVehicle = () => {
  if (!vehicle.vehicleType.trim()) {
    setMessage('Vehicle type is required.');
    return false;
  }

  if (!vehicle.make.trim()) {
    setMessage('Vehicle make is required.');
    return false;
  }

  if (!vehicle.model.trim()) {
    setMessage('Vehicle model is required.');
    return false;
  }

  if (!vehicle.year) {
    setMessage('Vehicle year is required.');
    return false;
  }

  if (!isValidVehicleYear(vehicle.year)) {
    setMessage(`Vehicle year must be between 1980 and ${currentYear + 1}.`);
    return false;
  }

  if (!vehicle.licensePlate.trim()) {
    setMessage('License plate is required.');
    return false;
  }

  if (vehicle.cargoCapacity && !isPositiveNumber(vehicle.cargoCapacity)) {
    setMessage('Cargo capacity must be a positive number.');
    return false;
  }

  return true;
};

const saveVehicle = () => {
  if (!validateVehicle()) return;
  setMessage('Vehicle information saved.');
};

  const saveCarrier = () => {
    if (!validateProfile()) return;
    setMessage('Carrier authority information saved.');
  };

  const fetchStripeStatus = async () => {
  try {
    const token =
      localStorage.getItem('courier_token') ||
      localStorage.getItem('token');

    if (!token) return;

    const response = await fetch(
      `${API}/api/courier/stripe/connect/status`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) return;

    const data = await response.json();
    setStripeStatus(data);
  } catch (error) {
    console.error('Stripe status error:', error);
  }
};

useEffect(() => {
  fetchStripeStatus();
}, []);

const handleStripeConnect = async () => {
  try {
    setStripeLoading(true);

    const token =
      localStorage.getItem('courier_token') ||
      localStorage.getItem('token');

    if (!token) {
      setMessage('Please sign in again before connecting payouts.');
      return;
    }

    const isStripeConnected =
      stripeStatus.onboardingComplete &&
      stripeStatus.chargesEnabled &&
      stripeStatus.payoutsEnabled;

    const endpoint =
      isStripeConnected || stripeStatus.connected
        ? '/api/courier/stripe/connect/manage'
        : '/api/courier/stripe/connect/start';

    const response = await fetch(`${API}${endpoint}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('Stripe Connect Response:', data);
      alert(JSON.stringify(data, null, 2));
      setMessage(
        data?.message ||
          data?.error ||
          'Unable to open secure payout setup.'
      );
      return;
    }

    const redirectUrl = data.onboardingUrl || data.url;

    if (!redirectUrl) {
      alert('No payout setup URL returned.');
      setMessage('Payout setup link was not returned.');
      return;
    }

    window.location.href = redirectUrl;
  } catch (error) {
    console.error('Stripe connect error:', error);
    setMessage('Unable to open secure payout setup.');
  } finally {
    setStripeLoading(false);
  }
};

const isStripeConnected =
  stripeStatus.onboardingComplete &&
  stripeStatus.chargesEnabled &&
  stripeStatus.payoutsEnabled;

const payoutAlertSeverity = isStripeConnected
  ? 'success'
  : stripeStatus.connected
    ? 'info'
    : 'warning';

const payoutStatusMessage = isStripeConnected
  ? 'Payout account connected. Bank payouts are enabled.'
  : stripeStatus.connected
    ? 'Payout setup started. Complete the secure setup to enable payments.'
    : 'Bank payouts are not set up yet.';

const payoutButtonText = stripeLoading
  ? 'Opening secure payout setup...'
  : isStripeConnected
    ? 'Manage Payout Account'
    : stripeStatus.connected
      ? 'Continue Payout Setup'
      : 'Set Up Bank Payouts';


  return (
    <CourierLayout>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '2rem',
            fontWeight: 700,
            lineHeight: 1.25,
            color: '#0f172a',
            mb: 1,
            textAlign: 'center',
          }}
        >
          Driver / Carrier Profile
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            textAlign: 'center',
            fontSize: '0.95rem',
            lineHeight: 1.5,
          }}
        >
          Complete required driver, insurance, vehicle, and carrier information.
        </Typography>
      </Box>

      {message && (
        <Alert
          severity={message.includes('saved') ? 'success' : 'warning'}
          sx={{
            mb: 2,
            borderRadius: 2,
          }}
        >
          {message}
        </Alert>
      )}

      <Paper
  elevation={1}
  sx={{
    p: { xs: 2, md: 2.5 },
    borderRadius: 3,
    border: '1px solid rgba(15, 23, 42, 0.08)',
    backgroundColor: '#ffffff',
    mb: 2,
  }}
>
  <Typography
    variant="subtitle1"
    sx={{
      color: '#0f172a',
      fontWeight: 700,
      mb: 0.75,
    }}
  >
    Payout Account
  </Typography>

  <Typography
    variant="body2"
    sx={{
      color: '#64748b',
      fontSize: '0.95rem',
      mb: 2,
    }}
  >
    Set up secure bank payouts so HaggleFreight can send payment for completed loads.
  </Typography>

  <Divider sx={{ mb: 2 }} />

    <Alert
      severity={payoutAlertSeverity}
      sx={{ mb: 2, borderRadius: 2 }}
    >
      {payoutStatusMessage}
    </Alert>

     <Button
      variant="contained"
      color="primary"
      onClick={handleStripeConnect}
      disabled={stripeLoading}
      sx={{
         minHeight: 42,
         px: 4,
         textTransform: 'none',
         fontWeight: 700,
         borderRadius: 2,
         width: { xs: '100%', sm: 'auto' },
       }}
     >
      {payoutButtonText}
     </Button>
</Paper>

      <Paper
        elevation={1}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: '1px solid rgba(15, 23, 42, 0.08)',
          backgroundColor: '#ffffff',
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: '#0f172a',
            fontWeight: 700,
            mb: 0.75,
          }}
        >
          Account & Compliance Details
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            fontSize: '0.95rem',
            mb: 2,
          }}
        >
          A valid driver license and active insurance are required before accepting loads.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
           <TextField
              label="Username"
              fullWidth
              value={profile.username}
              onChange={(event) =>
                handleProfileChange('username', event.target.value)
              }
              error={Boolean(profile.username) && !isValidUsername(profile.username)}
              helperText="3–30 characters. Letters, numbers, dots, underscores, or hyphens only."
           />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
               label="Email"
               type="email"
               fullWidth
               value={profile.email}
               onChange={(event) =>
                 handleProfileChange('email', event.target.value)
               }
               error={Boolean(profile.email) && !isValidEmail(profile.email)}
               helperText={
                 Boolean(profile.email) && !isValidEmail(profile.email)
                    ? 'Enter a valid email address.'
                    : ''
               }
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              label="Account Type"
              fullWidth
              value={profile.accountType}
              onChange={(event) =>
                handleProfileChange('accountType', event.target.value)
              }
            >
              <MenuItem value="Individual Driver">Individual Driver</MenuItem>
              <MenuItem value="Professional Carrier">
                Professional Carrier
              </MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Driver License Number"
              required
              fullWidth
              value={profile.driverLicenseNumber}
              onChange={(event) =>
                handleProfileChange('driverLicenseNumber', event.target.value)
              }
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
               select
               label="Issuing State"
               required
               fullWidth
               value={profile.driverLicenseState}
               onChange={(event) =>
                 handleProfileChange('driverLicenseState', event.target.value)
               }
               error={Boolean(profile.driverLicenseState) && !isValidStateCode(profile.driverLicenseState)}
               helperText="Select the state that issued your driver license."
            >
              {[
                 'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
                 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
                 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
                 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
                 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
                 'DC',
                ].map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
               ))}
            </TextField>

          </Grid>

          <Grid item xs={12} md={4}>
           <TextField
  label="License Expiration"
  type="date"
  required
  fullWidth
  InputLabelProps={{ shrink: true }}
  value={profile.driverLicenseExpiration}
  onChange={(event) =>
    handleProfileChange('driverLicenseExpiration', event.target.value)
  }
  error={Boolean(profile.driverLicenseExpiration) && !isFutureDate(profile.driverLicenseExpiration)}
  helperText={
    Boolean(profile.driverLicenseExpiration) && !isFutureDate(profile.driverLicenseExpiration)
      ? 'Expiration date must be in the future.'
      : ''
  }
/>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Insurance Policy Number"
              required
              fullWidth
              value={profile.insurancePolicyNumber}
              onChange={(event) =>
                handleProfileChange('insurancePolicyNumber', event.target.value)
              }
            />
          </Grid>

          <Grid item xs={12} md={4}>
           <TextField
  label="Insurance Expiration"
  type="date"
  required
  fullWidth
  InputLabelProps={{ shrink: true }}
  value={profile.insuranceExpiration}
  onChange={(event) =>
    handleProfileChange('insuranceExpiration', event.target.value)
  }
  error={Boolean(profile.insuranceExpiration) && !isFutureDate(profile.insuranceExpiration)}
  helperText={
    Boolean(profile.insuranceExpiration) && !isFutureDate(profile.insuranceExpiration)
      ? 'Expiration date must be in the future.'
      : ''
  }
/>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Lift & Carry Capacity (lbs)"
              type="number"
              fullWidth
              value={profile.liftCapacity}
              onChange={(event) =>
                handleProfileChange('liftCapacity', event.target.value)
              }
            />
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'stretch', sm: 'flex-end' },
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={saveProfile}
                sx={{
                  minHeight: 42,
                  px: 4,
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Save Profile
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper
        elevation={1}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: '1px solid rgba(15, 23, 42, 0.08)',
          backgroundColor: '#ffffff',
          mb: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            color: '#0f172a',
            fontWeight: 700,
            mb: 0.75,
          }}
        >
          Vehicle / Fleet Details
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            fontSize: '0.95rem',
            mb: 2,
          }}
        >
          Vehicle details help match drivers and carriers with the right loads.
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="Vehicle Type"
              fullWidth
              value={vehicle.vehicleType}
              onChange={(event) =>
                handleVehicleChange('vehicleType', event.target.value)
              }
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Make"
              fullWidth
              value={vehicle.make}
              onChange={(event) =>
                handleVehicleChange('make', event.target.value)
              }
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Model"
              fullWidth
              value={vehicle.model}
              onChange={(event) =>
                handleVehicleChange('model', event.target.value)
              }
            />
          </Grid>

          <Grid item xs={12} md={4}>
           <TextField
  label="Year"
  type="number"
  fullWidth
  value={vehicle.year}
  onChange={(event) =>
    handleVehicleChange('year', event.target.value)
  }
  inputProps={{
    min: 1980,
    max: currentYear + 1,
  }}
  error={Boolean(vehicle.year) && !isValidVehicleYear(vehicle.year)}
  helperText={`Enter a year between 1980 and ${currentYear + 1}.`}
/>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="Color"
              fullWidth
              value={vehicle.color}
              onChange={(event) =>
                handleVehicleChange('color', event.target.value)
              }
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              label="License Plate"
              fullWidth
              value={vehicle.licensePlate}
              onChange={(event) =>
                handleVehicleChange('licensePlate', event.target.value)
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Cargo Capacity"
              fullWidth
              value={vehicle.cargoCapacity}
              onChange={(event) =>
                handleVehicleChange('cargoCapacity', event.target.value)
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Equipment"
              fullWidth
              value={vehicle.equipment}
              onChange={(event) =>
                handleVehicleChange('equipment', event.target.value)
              }
            />
          </Grid>

          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: { xs: 'stretch', sm: 'flex-end' },
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={saveVehicle}
                sx={{
                  minHeight: 42,
                  px: 4,
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 2,
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Save Vehicle
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {isProfessionalCarrier && (
        <Paper
          elevation={1}
          sx={{
            p: { xs: 2, md: 2.5 },
            borderRadius: 3,
            border: '1px solid rgba(15, 23, 42, 0.08)',
            backgroundColor: '#ffffff',
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              color: '#0f172a',
              fontWeight: 700,
              mb: 0.75,
            }}
          >
            Carrier Authority
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: '#64748b',
              fontSize: '0.95rem',
              mb: 2,
            }}
          >
            Professional carriers must provide authority and registration details when applicable.
          </Typography>

          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Authority Number"
                fullWidth
                value={carrier.authorityNumber}
                onChange={(event) =>
                  handleCarrierChange('authorityNumber', event.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="DOT Number"
                fullWidth
                value={carrier.dotNumber}
                onChange={(event) =>
                  handleCarrierChange('dotNumber', event.target.value)
                }
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                label="MC Number"
                fullWidth
                value={carrier.mcNumber}
                onChange={(event) =>
                  handleCarrierChange('mcNumber', event.target.value)
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: { xs: 'stretch', sm: 'flex-end' },
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={saveCarrier}
                  sx={{
                    minHeight: 42,
                    px: 4,
                    textTransform: 'none',
                    fontWeight: 700,
                    borderRadius: 2,
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  Save Carrier Authority
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}
    </CourierLayout>
  );
};

export default CourierProfile;